import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UserCheck, FileText, Clock } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { AttendedCustomer } from '@/types/availability';
import { format, formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import { NotesDialog } from './NotesDialog';
import { Timestamp } from 'firebase/firestore';

interface MergedCustomer {
  customerId: string;
  customerName: string;
  customerPhone?: string;
  totalCalls: number;
  totalDuration: number; // Total minutes across all calls
  lastCallTime: Timestamp;
  firstCallTime: Timestamp;
}

interface AttendedCustomersCardProps {
  attendedCustomers: AttendedCustomer[];
  coachId: string;
}

export function AttendedCustomersCard({ attendedCustomers, coachId }: AttendedCustomersCardProps) {
  const { language } = useLanguage();
  const [selectedCustomer, setSelectedCustomer] = useState<MergedCustomer | null>(null);
  const [showNotesDialog, setShowNotesDialog] = useState(false);

  // Merge customers with same customerId
  const mergedCustomers = useMemo(() => {
    const customerMap = new Map<string, MergedCustomer>();
    
    attendedCustomers.forEach(customer => {
      const existing = customerMap.get(customer.customerId);
      
      if (existing) {
        // Merge with existing
        existing.totalCalls += 1;
        existing.totalDuration += customer.callDuration || 0;
        
        // Update last call time if this call is more recent
        if (customer.callEndTime.toMillis() > existing.lastCallTime.toMillis()) {
          existing.lastCallTime = customer.callEndTime;
        }
        
        // Update first call time if this call is earlier
        if (customer.callStartTime.toMillis() < existing.firstCallTime.toMillis()) {
          existing.firstCallTime = customer.callStartTime;
        }
      } else {
        // Add new customer
        customerMap.set(customer.customerId, {
          customerId: customer.customerId,
          customerName: customer.customerName,
          customerPhone: customer.customerPhone,
          totalCalls: 1,
          totalDuration: customer.callDuration || 0,
          lastCallTime: customer.callEndTime,
          firstCallTime: customer.callStartTime,
        });
      }
    });
    
    // Convert to array and sort by last call time (most recent first)
    return Array.from(customerMap.values()).sort(
      (a, b) => b.lastCallTime.toMillis() - a.lastCallTime.toMillis()
    );
  }, [attendedCustomers]);

  const handleOpenFile = (customer: MergedCustomer) => {
    setSelectedCustomer(customer);
    setShowNotesDialog(true);
  };

  // Create appointment object for NotesDialog
  const getAppointmentForDialog = (customer: MergedCustomer) => {
    return {
      id: `attended-${customer.customerId}-${customer.lastCallTime.toMillis()}`,
      customerId: customer.customerId,
      customerName: customer.customerName,
      customerPhone: customer.customerPhone,
      date: customer.lastCallTime.toDate(),
      coachId: coachId,
      status: 'completed' as const,
      notes: ''
    };
  };

  // Count unique customers vs total calls
  const uniqueCustomersCount = mergedCustomers.length;
  const totalCallsCount = attendedCustomers.length;

  return (
    <>
      <Card>
        <CardHeader className="p-3 md:p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-1.5 md:gap-2 text-base md:text-lg">
              <UserCheck className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
              <span>{language === 'ar' ? 'الحضور' : 'Attended'}</span>
              {uniqueCustomersCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {uniqueCustomersCount}
                  {totalCallsCount > uniqueCustomersCount && (
                    <span className="text-muted-foreground ml-1">
                      ({totalCallsCount} {language === 'ar' ? 'مكالمة' : 'calls'})
                    </span>
                  )}
                </Badge>
              )}
            </CardTitle>
          </div>
        </CardHeader>
        
        <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
          {mergedCustomers.length === 0 ? (
            <div className="text-center py-6 md:py-8 text-muted-foreground">
              <UserCheck className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-3 md:mb-4 opacity-50" />
              <p className="text-sm md:text-base">
                {language === 'ar' ? 'لم تتم خدمة أي مستفيد بعد اليوم' : 'No customers attended yet today'}
              </p>
            </div>
          ) : (
            <div className="space-y-2 md:space-y-3">
              {mergedCustomers.map((customer, index) => (
                <motion.div
                  key={customer.customerId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2.5 md:p-3 bg-green-50 border border-green-200 rounded-lg"
                >
                  <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <div className="w-7 h-7 md:w-8 md:h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <UserCheck className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-600" />
                    </div>
                    
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-sm md:text-base truncate">{customer.customerName}</h4>
                        {customer.totalCalls > 1 && (
                          <Badge variant="outline" className="text-xs bg-green-100 border-green-300">
                            {customer.totalCalls}x
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground flex-wrap">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span>
                            {language === 'ar' 
                              ? `${customer.totalDuration} دقيقة` 
                              : `${customer.totalDuration} min`
                            }
                            {customer.totalCalls > 1 && (
                              <span className="text-muted-foreground">
                                {language === 'ar' ? ' إجمالي' : ' total'}
                              </span>
                            )}
                          </span>
                        </div>
                        <span className="text-gray-400">•</span>
                        <span>
                          {formatDistanceToNow(customer.lastCallTime.toDate(), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleOpenFile(customer)}
                    className="w-full sm:w-auto text-xs md:text-sm h-8 md:h-9 border-green-300 hover:bg-green-100"
                  >
                    <FileText className="h-3 w-3 md:h-3.5 md:w-3.5 mr-1.5" />
                    {language === 'ar' ? 'فتح الملف' : 'Open File'}
                  </Button>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes Dialog */}
      {showNotesDialog && selectedCustomer && (
        <NotesDialog
          appointment={getAppointmentForDialog(selectedCustomer)}
          onOpenChange={(open) => {
            setShowNotesDialog(open);
            if (!open) {
              setSelectedCustomer(null);
            }
          }}
          onNotesUpdate={() => {}}
        />
      )}
    </>
  );
}
